import api from './api'

export const citaService = {
  async getAll(params = {}) {
    const response = await api.get('/citas', { params })
    return response.data
  },

  // Endpoint dedicado para dueños (si existe). Hace fallback a /citas filtrado por backend.
  async getCitasByDueno(params = {}) {
    try {
      const response = await api.get('/citas/mis-citas', { params })
      const raw = response.data
      const citas = Array.isArray(raw?.data)
        ? raw.data
        : (Array.isArray(raw?.data?.data) ? raw.data.data : (Array.isArray(raw) ? raw : []))
      return { success: true, data: citas }
    } catch (error) {
      if (error.response?.status === 404) {
        return this.getAll(params)
      }
      throw error
    }
  },

  async getById(id) {
    const response = await api.get(`/citas/${id}`)
    return response.data
  },

  /** Códigos y etiquetas de tipos de evento. */
  async getTipos() {
    const response = await api.get('/citas/tipos')
    return response.data
  },

  async create(data) {
    const response = await api.post('/citas', data)
    return response.data
  },

  async createEmergencia(data) {
    const response = await api.post('/citas/emergencia', data)
    return response.data
  },

  async update(id, data) {
    const response = await api.put(`/citas/${id}`, data)
    return response.data
  },

  async aprobar(id) {
    const response = await api.post(`/citas/${id}/aprobar`)
    return response.data
  },

  async rechazar(id, motivo) {
    const response = await api.post(`/citas/${id}/rechazar`, { motivo_rechazo: motivo })
    return response.data
  },

  // Asignar fecha y hora a una cita aprobada
  async asignarFechaHora(id, data) {
    const response = await api.post(`/citas/${id}/asignar-fecha-hora`, data)
    return response.data
  },

  // Completar una cita (solo veterinarios)
  async completar(id, data) {
    const response = await api.post(`/citas/${id}/completar`, data)
    return response.data
  },

  // Descargar archivo de calendario (.ics) para una cita confirmada
  // Compatible con Android, iOS y PC
  async descargarCalendario(id) {
    try {
      const response = await api.get(`/citas/${id}/calendario`, {
        responseType: 'blob', // Importante para descargar archivos
        headers: {
          'Accept': 'text/calendar, application/octet-stream, */*'
        }
      })
      
      // Verificar que la respuesta es un blob válido
      if (!(response.data instanceof Blob)) {
        // Intentar parsear como JSON si es un error
        const text = await response.data.text()
        try {
          const jsonError = JSON.parse(text)
          throw new Error(jsonError.error || jsonError.message || 'Error al descargar calendario')
        } catch (e) {
          throw new Error('Error: La respuesta no es un archivo de calendario válido')
        }
      }
      
      // Obtener el nombre del archivo del header Content-Disposition si está disponible
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      let filename = `cita-${id}.ics`
      if (contentDisposition) {
        // Manejar diferentes formatos: filename="..." o filename*=UTF-8''...
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i) ||
                             contentDisposition.match(/filename=([^;]+)/i)
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1].trim())
          // Limpiar comillas si las hay
          filename = filename.replace(/^["']|["']$/g, '')
        }
      }
      
      // Crear blob con el tipo MIME correcto
      // Android es muy estricto con el tipo MIME
      const blob = new Blob([response.data], { 
        type: 'text/calendar; charset=utf-8'
      })
      
      // Detectar si es un dispositivo móvil
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      const isAndroid = /Android/i.test(navigator.userAgent)
      const isXiaomi = /Xiaomi|Redmi|POCO|MIUI/i.test(navigator.userAgent) || /Mi\s/i.test(navigator.userAgent)
      
      if (isIOS) {
        // iOS requiere un enfoque diferente
        // Intentar abrir en una nueva ventana o usar el método de descarga nativo
        const url = window.URL.createObjectURL(blob)
        
        // Crear un enlace temporal
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        link.setAttribute('target', '_blank')
        
        // Para iOS, intentar abrir directamente
        document.body.appendChild(link)
        link.click()
        
        // Limpiar después de un delay
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }, 100)
      } else if (isAndroid || isXiaomi) {
        // Android y Xiaomi: el calendario de Xiaomi puede no soportar importación directa
        // Mejor opción: sincronizar con Google Calendar
        const url = window.URL.createObjectURL(blob)
        
        // Para Xiaomi, ofrecer opción de abrir en Google Calendar web
        if (isXiaomi) {
          // Leer el contenido para ofrecer opción de importar en Google Calendar
          const reader = new FileReader()
          reader.onload = function(e) {
            const icsContent = e.target.result
            
            // Mostrar mensaje informativo para Xiaomi
            console.log('📅 Dispositivo Xiaomi detectado')
            console.log('El calendario de Xiaomi puede no soportar importación directa de archivos .ics')
            console.log('Opciones:')
            console.log('1. Sincronizar con Google Calendar:')
            console.log('   - Abre https://calendar.google.com en tu navegador')
            console.log('   - Ve a Configuración > Importar y exportar')
            console.log('   - Selecciona el archivo descargado')
            console.log('2. Usar una app como ICSx⁵ desde F-Droid')
            console.log('3. Abrir el archivo desde Descargas y seleccionar "Abrir con Google Calendar"')
            
            // Intentar descargar el archivo
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            link.setAttribute('type', 'text/calendar')
            link.style.display = 'none'
            document.body.appendChild(link)
            
            try {
              link.click()
            } catch (e) {
              console.warn('Error con método de descarga:', e)
            }
            
            // Limpiar después de un delay
            setTimeout(() => {
              if (document.body.contains(link)) {
                document.body.removeChild(link)
              }
              window.URL.revokeObjectURL(url)
            }, 2000)
          }
          
          reader.onerror = function() {
            // Fallback: descarga simple
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
              if (document.body.contains(link)) {
                document.body.removeChild(link)
              }
              window.URL.revokeObjectURL(url)
            }, 100)
          }
          
          reader.readAsText(blob, 'UTF-8')
        } else {
          // Android estándar (no Xiaomi) - usar método estándar
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          link.setAttribute('type', 'text/calendar')
          link.style.display = 'none'
          document.body.appendChild(link)
          
          try {
            link.click()
          } catch (e) {
            console.warn('Error con método de descarga:', e)
          }
          
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link)
            }
            window.URL.revokeObjectURL(url)
          }, 500)
        }
      } else if (isMobile) {
        // Otros móviles
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }, 100)
      } else {
        // PC/Desktop - descarga estándar
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        setTimeout(() => {
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }, 100)
      }
      
      return { success: true, message: 'Calendario descargado exitosamente' }
    } catch (error) {
      // Manejar errores específicos
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.error || 'La cita debe tener fecha y hora para descargar calendario')
      } else if (error.response?.status === 403) {
        throw new Error('No tienes permisos para descargar este archivo de calendario')
      } else if (error.response?.status === 404) {
        throw new Error('Cita no encontrada')
      } else if (error.message) {
        throw error
      } else {
        throw new Error('Error al descargar el archivo de calendario')
      }
    }
  },

  // Obtener estadísticas de citas por clínica
  async estadisticasClinica(params = {}) {
    const response = await api.get('/citas/estadisticas/clinica', { params })
    return response.data
  },
}

